import { Dispatch } from 'redux'

import { useThunkActions } from 'redux/actions/useThunkActions'
import {
  cancelGeneration,
  getGeneration,
  getGenerations,
  startGeneration
} from 'redux/api/AnalysisTypes'
import {
  generate as generateAnalysis,
  getOne as getAnalysis
} from 'redux/api/Analyses'

import type { TGenerationInProgressErrorMeta } from '@homepal/api-contract'

import { parseApiErrors } from 'utils/errorParser'

import {
  AnalysisGeneration,
  AnalysisGenerationStatus,
  TERMINAL_GENERATION_STATUSES
} from 'types/AnalysisGeneration'

import * as actions from './actions'
import * as analysisActions from '../Analyses/actions'

export function tryGetGenerations() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getAll())

    getGenerations()
      .then((generations) => {
        dispatch(actions.getAllSuccess(generations))
      })
      .catch(() => {
        dispatch(actions.getAllFailed())
      })
  }
}

/**
 * A started run is followed like any other. So is one already going, when
 * the caller only wanted the analysis type to run.
 */
function followTriggeredRun(
  dispatch: Dispatch,
  request: Promise<AnalysisGeneration>,
  pickUpRunning: boolean,
  callback?: (err: boolean) => void
) {
  dispatch(actions.trigger())

  request
    .then((generation) => {
      dispatch(actions.triggerSuccess(generation))
      callback?.(false)
    })
    .catch((error) => {
      const inProgress = parseApiErrors(error).find(
        (entry) => entry.code === 'generation_in_progress'
      )?.meta as
        | TGenerationInProgressErrorMeta<AnalysisGenerationStatus>
        | undefined

      // Already running: pick up the run that is in progress.
      if (pickUpRunning && inProgress) {
        dispatch(actions.triggerSuccess(inProgress.generation))
        callback?.(false)

        return
      }

      dispatch(actions.triggerFailed())
      callback?.(true)
    })
}

function tryStartGeneration(
  analysisTypeId: string,
  callback?: (err: boolean) => void
) {
  return (dispatch: Dispatch) => {
    followTriggeredRun(
      dispatch,
      startGeneration(analysisTypeId),
      true,
      callback
    )
  }
}

/**
 * Reruns the analysis in place without these properties, the full list.
 * They reach the analysis only once the run succeeds. A run already going
 * is a failure here: it is not the one asked for.
 */
function tryRerunAnalysis(
  id: string,
  excluded: string[],
  callback?: (err: boolean) => void
) {
  return (dispatch: Dispatch) => {
    followTriggeredRun(
      dispatch,
      generateAnalysis(id, { excluded_real_estates: excluded }),
      false,
      callback
    )
  }
}

function tryCancelGeneration(
  generationId: string,
  onCancelled?: (generation: AnalysisGeneration) => void
) {
  return (dispatch: Dispatch) => {
    cancelGeneration(generationId)
      .then((generation) => {
        dispatch(actions.cancelSuccess(generation))
        onCancelled?.(generation)
      })
      .catch(() => {
        // Cancel failed — polling continues, the user can try again.
      })
  }
}

function tryPollGeneration(generationId: string) {
  return (dispatch: Dispatch) => {
    getGeneration(generationId)
      .then((generation) => {
        if (TERMINAL_GENERATION_STATUSES.includes(generation.status)) {
          dispatch(actions.finished(generation))
        }

        if (
          generation.status === AnalysisGenerationStatus.SUCCEEDED &&
          generation.analysis_id
        ) {
          dispatch(analysisActions.getOne())

          getAnalysis(generation.analysis_id)
            .then((response) => {
              dispatch(analysisActions.getOneSuccess(response.data))
            })
            .catch(() => {
              dispatch(analysisActions.getOneFailed())
            })
        }
      })
      .catch(() => {
        // Transient poll errors are fine — the next tick tries again.
      })
  }
}

export const useAnalysisGenerationActions = () => {
  return {
    tryGetGenerations: useThunkActions(tryGetGenerations),
    tryStartGeneration: useThunkActions(tryStartGeneration),
    tryRerunAnalysis: useThunkActions(tryRerunAnalysis),
    tryCancelGeneration: useThunkActions(tryCancelGeneration),
    tryPollGeneration: useThunkActions(tryPollGeneration),
    dismissCompleted: useThunkActions(actions.dismissCompleted),
    follow: useThunkActions(actions.follow),
    unfollow: useThunkActions(actions.unfollow)
  }
}
